import React, { useEffect, useState } from "react";
import { Smartphone, Monitor, Share, X } from "lucide-react";

const DISMISS_KEY = "forfy_install_banner_dismissed";

/**
 * Banner "Aggiungi a Home" (PWA).
 * Componente autonomo: rileva piattaforma, gestisce visibilita' e dismiss
 * senza bisogno di props esterne (a parte darkMode).
 *
 * - Se l'app e' gia' installata (standalone) non viene mai mostrato.
 * - "Ho capito" -> dismiss permanente (localStorage).
 * - "Ricordamelo dopo" -> dismiss solo per questo mount (ricompare al refresh/login).
 */
function detectPlatform() {
  if (typeof navigator === "undefined") return "generic";

  const ua = navigator.userAgent || navigator.vendor || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS 13+ si presenta come Mac ma ha il touch
  const isIPadOS = /Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document;
  const isAndroid = /Android/.test(ua);

  if (isIOS || isIPadOS) return "ios";
  if (isAndroid) return "android";
  if (/Chrome|Chromium|Edg/.test(ua)) return "desktop";
  return "generic";
}

function isStandalone() {
  if (typeof window === "undefined") return false;

  const matchesDisplayMode =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;

  // Safari iOS espone navigator.standalone
  const iosStandalone = window.navigator?.standalone === true;

  return Boolean(matchesDisplayMode || iosStandalone);
}

const PLATFORM_CONTENT = {
  ios: {
    icon: Share,
    title: "Aggiungi Forfy alla schermata Home",
    instruction: "Tocca l'icona Condividi in basso, poi \"Aggiungi a Home\".",
  },
  android: {
    icon: Smartphone,
    title: "Aggiungi Forfy alla schermata Home",
    instruction: "Tocca ⋮ in alto a destra, poi \"Aggiungi a schermata Home\".",
  },
  desktop: {
    icon: Monitor,
    title: "Installa Forfy sul tuo computer",
    instruction: "Clicca l'icona di installazione nella barra degli indirizzi.",
  },
  generic: {
    icon: Smartphone,
    title: "Aggiungi Forfy alla schermata Home",
    instruction: "Usa il menu del tuo browser per aggiungere Forfy alla schermata Home o installarlo.",
  },
};

export default function InstallAppBanner({ darkMode }) {
  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  const [alreadyDismissed, setAlreadyDismissed] = useState(true);
  const [platform, setPlatform] = useState("generic");
  const [standalone, setStandalone] = useState(true);

  useEffect(() => {
    setPlatform(detectPlatform());
    setStandalone(isStandalone());

    try {
      setAlreadyDismissed(window.localStorage.getItem(DISMISS_KEY) === "true");
    } catch {
      // localStorage non disponibile (es. modalita' privata restrittiva): non bloccare il banner
      setAlreadyDismissed(false);
    }
  }, []);

  if (standalone) return null;
  if (alreadyDismissed) return null;
  if (dismissedThisSession) return null;

  const handleGotIt = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // ignora errori di scrittura localStorage
    }
    setAlreadyDismissed(true);
  };

  const handleRemindLater = () => {
    setDismissedThisSession(true);
  };

  const { icon: Icon, title, instruction } = PLATFORM_CONTENT[platform] || PLATFORM_CONTENT.generic;

  return (
    <div
      className={`relative rounded-xl border p-4 sm:p-5 ${
        darkMode
          ? "bg-neutral-900 border-forfy-700/60"
          : "bg-forfy-50 border-forfy-200"
      }`}
      role="region"
      aria-label="Istruzioni per installare l'app"
    >
      <button
        type="button"
        onClick={handleRemindLater}
        aria-label="Chiudi"
        className={`absolute top-3 right-3 p-1 rounded-lg transition-colors ${
          darkMode
            ? "text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300"
            : "text-neutral-400 hover:bg-white hover:text-neutral-600"
        }`}
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div
          className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            darkMode ? "bg-forfy-900/40" : "bg-forfy-100"
          }`}
        >
          <Icon className={`w-5 h-5 ${darkMode ? "text-forfy-300" : "text-forfy-700"}`} />
        </div>

        <div className="min-w-0">
          <h3 className={`text-sm font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
            {title}
          </h3>
          <p className={`mt-1 text-sm ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
            {instruction}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleGotIt}
              className="px-3 py-1.5 rounded-lg bg-forfy-500 text-white text-sm font-medium hover:bg-forfy-600 transition-colors"
            >
              Ho capito
            </button>
            <button
              type="button"
              onClick={handleRemindLater}
              className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                darkMode
                  ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                  : "border-forfy-200 bg-white text-neutral-700 hover:bg-forfy-100"
              }`}
            >
              Ricordamelo dopo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
